import { useCallback, useMemo, useRef, useState } from 'react';
import type { DateTime } from 'luxon';
import {
  CalendarDragContextValue,
  DropTarget,
  DropTargetKey,
  computeDroppedDate,
  pointInRect,
} from './calendar.drag';

/**
 * Controller behind the calendar drag (plan 0017 §3, P3.S1; live-rect fix
 * gripp-uzs.15). Owns the drop-target registry, the in-flight drag state, and the
 * hit-test that maps a pointer position → the target under it. Produces the
 * `CalendarDragContextValue` the grids + pills consume.
 *
 * Targets live in a ref (registry is imperative, mutated from many ref callbacks
 * — not render state). Only `activeDropKey` + `draggingId` are React state, since
 * they drive the highlight + origin-pill lift. Each target's geometry is read
 * LIVE via `getRect()` (getBoundingClientRect) at hit-test time, so transforms
 * (SlideLayout slide-in), scroll, and re-layout never make it stale. All coords
 * are CLIENT coords to match getBoundingClientRect's viewport frame.
 */
export const useCalendarDragController = (
  enabled: boolean,
  onEventDrop?: (eventId: string, newDate: DateTime) => void,
): CalendarDragContextValue => {
  const targetsRef = useRef<Map<DropTargetKey, DropTarget>>(new Map());
  const dragRef = useRef<{ eventId: string; at: DateTime } | null>(null);
  const [activeDropKey, setActiveDropKey] = useState<DropTargetKey | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);

  const registerTarget = useCallback((key: DropTargetKey, target: DropTarget) => {
    targetsRef.current.set(key, target);
  }, []);

  const unregisterTarget = useCallback((key: DropTargetKey) => {
    targetsRef.current.delete(key);
  }, []);

  /**
   * Find the registry key whose LIVE rect contains the client point, or null.
   * Reads each target's getBoundingClientRect fresh, so the result reflects the
   * current transform/scroll (the gripp-uzs.15 fix for stale rects).
   */
  const hitTest = useCallback((clientX: number, clientY: number): DropTargetKey | null => {
    for (const [key, target] of targetsRef.current) {
      const rect = target.getRect();
      if (rect && pointInRect(rect, clientX, clientY)) return key;
    }
    return null;
  }, []);

  const startDrag = useCallback(
    (eventId: string, at: DateTime, clientX: number, clientY: number) => {
      dragRef.current = { eventId, at };
      setDraggingId(eventId);
      setActiveDropKey(hitTest(clientX, clientY));
    },
    [hitTest],
  );

  const updateDrag = useCallback(
    (clientX: number, clientY: number) => {
      if (!dragRef.current) return;
      const key = hitTest(clientX, clientY);
      // Avoid redundant setState when the pointer stays in the same cell.
      setActiveDropKey((prev) => (prev === key ? prev : key));
    },
    [hitTest],
  );

  const reset = useCallback(() => {
    dragRef.current = null;
    setDraggingId(null);
    setActiveDropKey(null);
  }, []);

  const endDrag = useCallback(
    (clientX: number, clientY: number) => {
      const drag = dragRef.current;
      if (!drag) {
        reset();
        return;
      }
      const key = hitTest(clientX, clientY);
      const target = key ? targetsRef.current.get(key) : undefined;
      if (target) {
        const newDate = computeDroppedDate(drag.at, target);
        // Skip a no-op drop back onto the same day+hour the event already has.
        const sameDay = newDate.hasSame(drag.at, 'day');
        const sameHour = target.hour === undefined || target.hour === drag.at.hour;
        if (!(sameDay && sameHour)) {
          onEventDrop?.(drag.eventId, newDate);
        }
      }
      reset();
    },
    [hitTest, onEventDrop, reset],
  );

  const cancelDrag = useCallback(() => {
    reset();
  }, [reset]);

  return useMemo(
    () => ({
      enabled,
      registerTarget,
      unregisterTarget,
      startDrag,
      updateDrag,
      endDrag,
      cancelDrag,
      activeDropKey,
      draggingId,
    }),
    [
      enabled,
      registerTarget,
      unregisterTarget,
      startDrag,
      updateDrag,
      endDrag,
      cancelDrag,
      activeDropKey,
      draggingId,
    ],
  );
};
